import type { ViewResponse } from "@redotech/effect-api-shared/rpc/view-folder";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useSupportTicketQueryScope } from "@redotech/redo-merchant-app-common/support/use-support-ticket-query-scope";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  buildCustomSupportTicketCountViews,
  buildDefaultSupportTicketCountViews,
  type SupportTicketCountSourceView,
  type SupportTicketCountView,
} from "@redotech/redo-support-v2/query/support-ticket-count-query";
import { supportTicketCountsScopedQueryKey } from "@redotech/redo-support-v2/query/support-ticket-query-keys";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { catchApiErrorsSilent } from "../db/error-handling";

/**
 * Counts however many views the caller asks for, in a single request. Callers
 * decide the breadth: the sidebar wants every view, the search empty state
 * wants one.
 */
export function useSupportTicketCountsForViews(
  countViews: SupportTicketCountView[],
): Map<string, number> {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const queryScope = useSupportTicketQueryScope();

  const { data } = useQuery({
    queryKey: [
      ...supportTicketCountsScopedQueryKey(queryScope),
      countViews.map((v) => [v.viewKey, v.filterGroup]),
    ],
    queryFn: async () => {
      const client = effectApi.getClient();
      const result = await effectApi.runPromise(
        catchApiErrorsSilent(
          client.supportTicketCounts.getSupportTicketCounts({
            payload: { views: countViews },
          }),
        ),
      );
      return result as Record<string, number>;
    },
    enabled: countViews.length > 0,
    staleTime: 0,
    meta: { showErrorToast: true },
  });

  return useMemo(() => {
    const map = new Map<string, number>();
    if (data) {
      for (const [key, count] of Object.entries(data)) {
        map.set(key, count);
      }
    }
    return map;
  }, [data]);
}

export function useSupportTicketCounts(
  views: ViewResponse[],
): Map<string, number> {
  const user = useRequiredContext(UserContext);
  const userId = user._id.toString();

  const viewsForCounts = useMemo(() => {
    return [
      ...buildDefaultSupportTicketCountViews(userId),
      ...buildCustomSupportTicketCountViews(
        views as readonly SupportTicketCountSourceView[],
      ),
    ];
  }, [views, userId]);

  return useSupportTicketCountsForViews(viewsForCounts);
}
